// Canvas OrdB_SupplierDetail: status strip, main / payment / contacts / delivery cards.
// B40: status comes from the network (ACTIVE / INACTIVE) and an inactive link locks «Захиалга үүсгэх».
// B41/B42: payment term shows its description and paymentDay, and every attached term with its discount.
import { Avatar, Box, Button, Text } from "@mantine/core"
import {
  ArrowLeftIcon,
  FileTextIcon,
  MoneyIcon,
  ShoppingCartIcon,
  TruckIcon,
  UserIcon,
  UsersIcon,
} from "@phosphor-icons/react"
import { ReactNode, useEffect } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { DotPill } from "../delivery/atoms"
import { DisabledReason, HelpButtonGroup, HelpIcon, StatusBadgeHelp, useHelpContext } from "@dehub/theme"
import { usePermission } from "../permission"
import { currencyFormat } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import classes from "./detail-form.module.css"

type Props = {
  action: any[]
}

/** ACTIVE / INACTIVE from the BE; legacy Mongolian literals are still read. */
const networkState = (status?: string | null): "active" | "inactive" | "pending" | "other" => {
  const upper = status?.toUpperCase()
  const lower = status?.toLowerCase()
  if (upper === "ACTIVE" || lower === "идэвхтэй") return "active"
  if (upper === "INACTIVE" || lower === "идэвхгүй") return "inactive"
  if (upper === "PENDING" || lower === "хүлээгдэж буй") return "pending"
  return "other"
}

function Card({ icon, title, helpKey, children }: Readonly<{ icon: ReactNode; title: string; helpKey?: string; children: ReactNode }>) {
  return (
    <Box className={classes.card}>
      <Box className={classes.cardHead}>
        <Box className={classes.iconTile}>{icon}</Box>
        <h2 className={classes.cardTitle}>{title}</h2>
        {/* @dehub/help R2b: one section «?» per card title. */}
        {helpKey && <HelpIcon helpKey={helpKey} variant="section" />}
      </Box>
      {children}
    </Box>
  )
}

function Field({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Box>
      <Text className={classes.label}>{label}</Text>
      <Box className={classes.value}>{children}</Box>
    </Box>
  )
}

export function BuyerCustomerDetailForm({ action }: Props) {
  const breadcrumbs = useBreadcrumb()
  const navigate = useNavigate()
  const { t } = useTranslation(["order", "common"])
  const record = action[1]

  const state = networkState(record?.networkStatus)
  const statusTone = state === "active" ? "green" : state === "pending" ? "amber" : "grey"
  const statusName =
    state === "active"
      ? t("common:status.active")
      : state === "inactive"
        ? t("common:status.inactive")
        : state === "pending"
          ? t("common:status.pending")
          : record?.networkStatus || "-"
  // canOrder comes from the BE; an inactive status locks it even if an older BE omits the flag.
  const canOrder = record?.canOrder !== false && state !== "inactive"
  const ORD_NET_NEW_ORDER = usePermission("ORD_NET_NEW_ORDER")
  const orderReasonCode = !ORD_NET_NEW_ORDER.hasAccess ? "PERM" : canOrder ? null : "INACTIVE"
  const statusCode = state === "active" ? "ACTIVE" : state === "inactive" ? "INACTIVE" : state === "pending" ? "PENDING" : undefined

  // @dehub/help: the link status drives the drawer's status tab (order.network-status, BUYER rows).
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ status: statusCode })
  }, [statusCode, setHelpContext])

  const term = record?.paymentTerm
  const termDays = term?.paymentDay
  const terms: any[] = Array.isArray(record?.paymentTerms) ? record.paymentTerms : []
  const supplierName = renderBusinessName(record, t("order:buyerCustomer.supplierInfo"))

  const discountText = (discount: any) => {
    if (!discount?.discountValue) return null
    const value = discount.discountType === "PERCENTAGE" ? `${discount.discountValue}%` : currencyFormat(discount.discountValue)
    return t("order:buyerCustomer.termDiscount", { value })
  }

  const staffRow = (staff: any, label: string) => {
    const contact = [staff?.email, staff?.phone].filter(Boolean).join(" · ")
    return (
      <Box className={classes.staff} key={label}>
        <Avatar src={staff?.avatar} size="md" radius="xl">
          <UserIcon size={16} />
        </Avatar>
        <Box style={{ minWidth: 0 }}>
          <Text className={classes.label}>{label}</Text>
          <Text className={classes.value}>{staff ? renderName(staff) : "-"}</Text>
          {contact && <Text className={classes.label}>{contact}</Text>}
        </Box>
      </Box>
    )
  }

  return (
    <PageLayout
      title={
        <Box style={{ display: "flex", alignItems: "center", gap: "var(--mantine-spacing-sm)" }}>
          <Avatar size={48} radius="md" color="brand" variant="filled">
            {String(supplierName || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Text fz="var(--nw-fz-title)" fw={700} lh={1.2}>
              {supplierName}
            </Text>
            <Text className={`${classes.label} ${classes.mono}`}>#{record?.refCode || "-"}</Text>
          </Box>
        </Box>
      }
      subTitle=""
      breadcrumb={breadcrumbs}
      extra={[
        <Button
          key="back"
          variant="default"
          leftSection={<ArrowLeftIcon size={16} />}
          onClick={() => navigate("/buyer-customer")}>
          {t("common:actions.back")}
        </Button>,
        <DisabledReason
          key="order"
          helpKey="order.buyer-customer-detail.reason.create-order"
          reason={orderReasonCode}
          when={!!orderReasonCode}
          position="bottom-end">
          <HelpButtonGroup helpKey="order.buyer-customer-detail.action.create-order">
            <Button
              data-help-id="create-order"
              leftSection={<ShoppingCartIcon size={16} />}
              onClick={() => navigate(`/buyer-customer/order/${record?.id}`)}>
              {t("order:buyerCustomer.createOrder")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}>
      <Box className={classes.strip}>
        <Box style={{ display: "flex", alignItems: "center", gap: "var(--mantine-spacing-md)", flexWrap: "wrap" }}>
          {statusCode ? (
            <StatusBadgeHelp helpNs="order.network-status" code={statusCode} name={statusName}>
              <DotPill tone={statusTone}>{statusName}</DotPill>
            </StatusBadgeHelp>
          ) : (
            <DotPill tone={statusTone}>{statusName}</DotPill>
          )}
          <Text className={classes.label}>{t("order:buyerCustomer.partner")}:</Text>
          <Text className={classes.value}>{renderBusinessName(record?.partner)}</Text>
          <Text className={`${classes.label} ${classes.mono}`}>#{record?.partner?.refCode || "-"}</Text>
        </Box>
        <Text className={classes.label}>
          {t("order:buyerCustomer.registered")}:{" "}
          <Text span className={`${classes.value} ${classes.mono}`}>
            {record?.createdAt ? dateTimeSecFormat(new Date(record.createdAt)) : "-"}
          </Text>
        </Text>
      </Box>

      <Box className={classes.grid}>
        <Box className={classes.col}>
          <Card icon={<FileTextIcon size={18} />} title={t("order:buyerCustomer.mainInfo")} helpKey="order.buyer-customer-detail.section.main-info">
            <Box className={classes.fields} data-cols="3">
              <Field label={t("order:buyerCustomer.supplierName")}>
                {supplierName}
                <Text className={`${classes.label} ${classes.mono}`}>#{record?.refCode || "-"}</Text>
              </Field>
              <Field label={t("order:buyerCustomer.partnerName")}>
                {renderBusinessName(record?.partner)}
                <Text className={`${classes.label} ${classes.mono}`}>#{record?.partner?.refCode || "-"}</Text>
              </Field>
              <Field label={t("order:buyerCustomer.taxId")}>
                <Text span className={classes.mono}>{record?.regNumber || "-"}</Text>
              </Field>
            </Box>
          </Card>

          <Card icon={<MoneyIcon size={18} />} title={t("order:buyerCustomer.paymentInfo")} helpKey="order.buyer-customer-detail.section.payment">
            <Box className={classes.fields}>
              <Field label={t("order:buyerCustomer.paymentTerm")}>{term?.description || "-"}</Field>
              <Field label={t("order:buyerCustomer.paymentDueDate")}>
                {termDays ? `${termDays} ${t("order:buyerCustomer.days")}` : "-"}
              </Field>
            </Box>
            <Box mt="md">
              <Text className={classes.label} mb={4}>{t("order:buyerCustomer.paymentTerms")}</Text>
              {terms.length === 0 ? (
                <Text className={classes.value}>-</Text>
              ) : (
                terms.map((row) => {
                  const discount = discountText(row.discount)
                  return (
                    <Box className={classes.termRow} key={row.id}>
                      <Box style={{ minWidth: 0 }}>
                        <Text className={classes.value}>{row.description || "-"}</Text>
                        {row.paymentDay ? (
                          <Text className={classes.label}>
                            {t("order:buyerCustomer.paymentDueDate")}: {row.paymentDay} {t("order:buyerCustomer.days")}
                          </Text>
                        ) : null}
                      </Box>
                      <Box style={{ display: "flex", gap: "var(--mantine-spacing-xs)", flexWrap: "wrap" }}>
                        {row.isDefault ? <DotPill tone="blue">{t("order:buyerCustomer.defaultTerm")}</DotPill> : null}
                        {discount ? <DotPill tone="green">{discount}</DotPill> : null}
                      </Box>
                    </Box>
                  )
                })
              )}
            </Box>
          </Card>
        </Box>

        <Box className={classes.col}>
          <Card icon={<UsersIcon size={18} />} title={t("order:buyerCustomer.contactStaff")} helpKey="order.buyer-customer-detail.section.contacts">
            {staffRow(record?.supplierStaff, t("order:buyerCustomer.supplierManager"))}
            {staffRow(record?.buyerStaff, t("order:buyerCustomer.buyerManager"))}
            {staffRow(record?.receiverStaff, t("order:buyerCustomer.receiverStaff"))}
          </Card>

          <Card icon={<TruckIcon size={18} />} title={t("order:buyerCustomer.deliveryInfo")} helpKey="order.buyer-customer-detail.section.delivery">
            <Box className={classes.fields}>
              <Field label={t("order:buyerCustomer.receiverBranch")}>
                {record?.receiverBranch?.name || "-"}
                {record?.receiverBranch?.refCode && (
                  <Text className={`${classes.label} ${classes.mono}`}>#{record.receiverBranch.refCode}</Text>
                )}
              </Field>
              <Field label={t("order:buyerCustomer.receiverAddress")}>
                {record?.receiverBranch?.branchAddress || "-"}
              </Field>
            </Box>
          </Card>
        </Box>
      </Box>
    </PageLayout>
  )
}

const useBreadcrumb = () => {
  const { t } = useTranslation(["order", "common"])
  return [
    {
      to: "/",
      label: t("common:breadcrumb.home"),
    },
    {
      to: "/buyer-customer",
      label: t("order:buyerCustomer.title"),
    },
    {
      label: t("common:breadcrumb.detail"),
    },
  ]
}
